import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";

import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { Newspaper, Pencil, Plus } from "@phosphor-icons/react";
import { useEffect, useState } from "react";

import { format, isValid } from "date-fns";
import { Calendar as CalendarIcon } from "lucide-react";

import { Calendar } from "@/components/ui/calendar";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { ScrollArea } from "@/components/ui/scroll-area";
import { useFundingType } from "@/hooks/useFundingType";
import { cn } from "@/lib/utils";
import { addVcStartupNews, editVcStartupNews } from "@/services/api/Startup";
import { createSimpleInvestor } from "@/services/api/VentureCapital";
import dayjs from "dayjs";
import { useRouter } from "next/router";
import { toast } from "react-hot-toast";

export const AddEditNews = ({
  startup,
  fundingRound,
  isTable,
  editedNews,
  closeParent,
}) => {
  const [date, setDate] = useState();
  const [selected, setSelected] = useState([]);
  const [selectedData, setSelectedData] = useState([]);
  const [selectedVc, setSelectedVc] = useState([]);
  const [query, setQuery] = useState("");
  const [investors, setInvestors] = useState([]);
  const { data: fundingTypes } = useFundingType();
  const [selectedFunding, setSelectedFunding] = useState(null);
  const [publisherName, setPublisherName] = useState("");
  const [publisherLink, setPublisherLink] = useState("");
  const [title, setTitle] = useState("");
  const [content, setContent] = useState("");
  const router = useRouter();

  const [value, setValue] = useState({
    startDate: null,
    endDate: null,
  });
  const changeDatepickerValue = (newValue) => {
    setValue(newValue);
  };

  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    if (editedNews) {
      setPublisherName(editedNews.publisher_name);
      setPublisherLink(editedNews.publisher_link);
      setTitle(editedNews.title);
      setContent(editedNews.content);
      setDate(new Date(editedNews.published_at));

      // Add safe parsing for vc_ids
      try {
        const vcIds = editedNews.vc_ids ? JSON.parse(editedNews.vc_ids) : [];
        setSelectedVc(Array.isArray(vcIds) ? vcIds : []);
      } catch (error) {
        console.warn("Error parsing vc_ids:", error);
        setSelectedVc([]);
      }
    }
  }, [editedNews]);

  const handleSubmit = async () => {
    if (!selectedFunding) return toast.error("Funding Round is required!");
    if (!date) return toast.error("Published At is required!");
    if (!publisherName) return toast.error("Publisher Name is required!");
    if (!publisherLink) return toast.error("Publisher Link is required!");
    if (!title) return toast.error("Title is required!");
    if (!content) return toast.error("Content is required!");

    const payload = {
      funding_round: fundingRound.id,
      published_at: dayjs(date).format("YYYY-MM-DD 00:00:00"),
      startup_id: startup.id,
      vc_ids: selectedVc,
      publisher_name: publisherName,
      publisher_link: publisherLink,
      title,
      content,
    };

    setIsLoading(true);

    try {
      if (editedNews) {
        await editVcStartupNews(editedNews.id, payload);
        toast.success("Successfully updated startup news!");
      } else {
        await addVcStartupNews(payload);
        toast.success("Successfully added startup news!");
      }

      setTimeout(() => {
        router.reload();
      }, 200);
    } catch (error) {
      toast.error("An error occurred while saving the news.");
      console.error(error);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (fundingRound) {
      setDate(new Date(fundingRound.date_announced));
      setSelectedFunding(fundingRound.funding_type_id);
      setSelected(fundingRound.investors);
      // If there's no editedNews, set all investors as selected by default
      if (!editedNews) {
        setSelectedVc(fundingRound.investors.map((investor) => investor.id));
      }
    }
  }, [fundingRound, editedNews]);

  const createSimpleVC = async () => {
    const response = await createSimpleInvestor({ name: query });
    setSelected((state) => [...state, response.data.data]);
  };

  const onClickVC = (id) => {
    setSelectedVc((prevSelected) => {
      if (prevSelected.includes(id)) {
        return prevSelected.filter((vcId) => vcId !== id);
      } else {
        return [...prevSelected, id];
      }
    });
  };

  return (
    <Dialog className='overflow-x-auto'>
      <DialogTrigger asChild>
        {isTable ? (
          <Button size='sm' className='h-auto px-2 py-1.5 text-xs'>
            <Plus className='mr-1.5' />
            <Newspaper className='w-4 h-4' />
          </Button>
        ) : (
          <button
            type='button'
            className='inline-flex items-center justify-center border border-gray-200 rounded-full w-9 h-9'
          >
            {fundingRound ? (
              <Pencil weight='fill' className='w-3.5 h-3.5' />
            ) : (
              <Plus weight='fill' className='w-3.5 h-3.5' />
            )}
          </button>
          //  <Button
          //    size='icon'
          //    className='relative w-10 h-10 text-xl text-white rounded-full bg-gradient-to-tr from-yellow-1 to-red-1'
          //  >
          //    <span className='absolute inline-flex w-full h-full rounded-full animate-ping bg-orange-1/50'></span>

          //    {fundingRound ? <Pencil weight='fill' /> : <Plus />}
          //  </Button>
        )}
      </DialogTrigger>
      <DialogContent className='max-w-2xl bg-white text-background max-h-[90vh]'>
        <DialogHeader>
          <DialogTitle>{editedNews ? "Edit" : "Add"} News</DialogTitle>
          <DialogDescription>
            <div className='grid flex-1 grid-cols-12 mt-5 gap-x-3 gap-y-4'>
              <div className='w-full col-span-6 space-y-1.5'>
                <Label className='text-xs text-background'>Funding Round</Label>
                <Select
                  id='established'
                  onValueChange={(value) => setSelectedFunding(value)}
                  disabled
                  value={fundingRound?.funding_type_id}
                >
                  <SelectTrigger className='bg-white text-background border-background/20 focus:ring-0'>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className='bg-white text-background border-background/20'>
                    <ScrollArea className='h-40'>
                      {fundingTypes.map((f) => (
                        <SelectItem
                          value={f.id}
                          key={`${f.id}`}
                          className='data-[highlighted]:text-white'
                        >
                          {f.name}
                        </SelectItem>
                      ))}
                    </ScrollArea>
                  </SelectContent>
                </Select>
                {/* <select
                  className='block w-full h-10 px-4 text-white border rounded-md bg-white/5 border-background/20 focus:border-orange-1 focus:ring-transparent sm:text-sm'
                  value={selectedFunding}
                  onChange={(e) => setSelectedFunding(e.target.value)}
                >
                  <option></option>
                  {fundingTypes.map((f) => (
                    <option key={f.id} value={f.id}>
                      {f.name}
                    </option>
                  ))}
                </select> */}
              </div>
              <div className='w-full col-span-6 space-y-1.5'>
                <Label
                  className='text-xs text-background'
                  htmlFor='headquarter'
                >
                  Published At
                </Label>
                <Popover modal={true}>
                  <PopoverTrigger asChild>
                    <Button
                      variant={"outline"}
                      className={cn(
                        "bg-white hover:bg-white w-full text-background justify-start text-left font-normal border-background/20",
                        !date && "text-muted-foreground"
                      )}
                    >
                      <CalendarIcon className='w-4 h-4 mr-2' />
                      {date && isValid(date) ? (
                        format(date, "PPP")
                      ) : (
                        <span>Pick a date</span>
                      )}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className='w-auto p-0'>
                    <Calendar
                      mode='single'
                      selected={date}
                      onSelect={setDate}
                      initialFocus
                      enableYearPicker
                      fromYear={1970}
                      toYear={new Date().getFullYear()}
                    />
                  </PopoverContent>
                </Popover>
              </div>
              <div className='w-full col-span-6 space-y-1.5'>
                <Label className='text-xs text-background'>
                  Publisher Name
                </Label>
                <div className='relative h-10'>
                  <Input
                    type='text'
                    className='absolute top-0 bg-white pr-11 border-background/20 focus-visible:ring-0 text-background'
                    value={publisherName}
                    onChange={(e) => setPublisherName(e.target.value)}
                  />
                </div>
              </div>
              <div className='w-full col-span-6 space-y-1.5'>
                <Label className='text-xs text-background'>
                  Publisher Link
                </Label>
                <div className='relative h-10'>
                  <Input
                    type='text'
                    className='absolute top-0 bg-white pr-11 border-background/20 focus-visible:ring-0 text-background'
                    value={publisherLink}
                    onChange={(e) => setPublisherLink(e.target.value)}
                  />
                </div>
              </div>
              <div className='w-full col-span-12 space-y-1.5'>
                <Label className='text-xs text-background'>News Title</Label>
                <div className='relative h-10'>
                  <Input
                    type='text'
                    className='absolute top-0 bg-white pr-11 border-background/20 focus-visible:ring-0 text-background'
                    value={title}
                    onChange={(e) => setTitle(e.target.value)}
                  />
                </div>
              </div>
              <div className='w-full col-span-12 space-y-1.5'>
                <Label className='text-xs text-background'>
                  News Content (Summary)
                </Label>
                <div className='relative h-10'>
                  <Textarea
                    type='text'
                    className='absolute top-0 bg-white pr-11 border-background/20 focus-visible:ring-0 text-background'
                    value={content}
                    onChange={(e) => setContent(e.target.value)}
                  />
                </div>
              </div>
              <div className='col-span-12 mt-12'>
                <div className='flex items-center justify-between'>
                  <Label className='text-xs text-background'>
                    VC Mentioned{" "}
                    {selectedVc.length > 0 && (
                      <span>({selectedVc.length})</span>
                    )}
                  </Label>
                  <Button
                    variant='outline'
                    size='sm'
                    className='h-auto px-3 py-1 text-xs'
                    onClick={() => {
                      if (selectedVc.length === selected.length) {
                        setSelectedVc([]);
                      } else {
                        setSelectedVc(selected.map((investor) => investor.id));
                      }
                    }}
                  >
                    {selectedVc.length === selected.length
                      ? "Deselect All"
                      : "Select All"}
                  </Button>
                </div>

                <div className='flex flex-wrap items-center gap-1 mt-2'>
                  {selected.map((s) => (
                    <Badge
                      key={s.id}
                      className={`border-gray-200 cursor-pointer ${
                        selectedVc.includes(s.id)
                          ? "bg-background text-white hover:bg-background hover:text-white hover:border-background"
                          : ""
                      }`}
                      onClick={() => onClickVC(s.id)}
                    >
                      {s.name}
                    </Badge>
                  ))}
                </div>
                <p className='mt-3 text-xs text-muted-foreground'>
                  Select minimum one VC
                </p>
              </div>
              <Button
                onClick={handleSubmit}
                size={"lg"}
                variant='secondary'
                className='col-span-12 font-medium'
                disabled={isLoading}
              >
                {isLoading ? (
                  <span className='flex items-center'>
                    <svg
                      className='w-5 h-5 mr-3 -ml-1 text-white animate-spin'
                      xmlns='http://www.w3.org/2000/svg'
                      fill='none'
                      viewBox='0 0 24 24'
                    >
                      <circle
                        className='opacity-25'
                        cx='12'
                        cy='12'
                        r='10'
                        stroke='currentColor'
                        strokeWidth='4'
                      ></circle>
                      <path
                        className='opacity-75'
                        fill='currentColor'
                        d='M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z'
                      ></path>
                    </svg>
                    {editedNews ? "Updating" : "Adding"}...
                  </span>
                ) : (
                  <>{editedNews ? "Update" : "Add"} News</>
                )}
              </Button>
            </div>
          </DialogDescription>
        </DialogHeader>
      </DialogContent>
    </Dialog>
  );
};
